/* eslint-disable operator-linebreak */
import React, { useCallback, useEffect, useState } from 'react';
import { FormSpy } from 'react-final-form';
import { FormApi, FormState } from 'final-form';
import { CircularProgress } from '@material-ui/core';
import { observer } from 'mobx-react';
import { toJS } from 'mobx';
import { useHistory } from 'react-router-dom';
import routePaths from 'routes/routePaths';
import accountsStore from 'store/accountsStore';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import Loader from 'ui/Loader/Loader';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { ITransactionField } from 'ui/structure/TransactionCard';
import { OTPConfirmationModal, StatusModal, WarningModal } from 'components/modals';
import { AxiosStatus } from 'api/axiosConfig';
import api from 'api/api';
import { getTransactionsArray, transactionValuesMapper } from 'utils/mapping/transactionCardMapper';
import auth from 'utils/auth';
import { IApiResponse } from 'utils/models';
import { NETWORK_REQUIRED, PHONE_LENGTH } from 'utils/constants';
import validateOtpPin, { handlePinError } from 'utils/otpValidation';
import { changeFieldValue } from 'utils/formMutators';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import renderWalletField from './MobileMoneyTransferFields';
import {
  IWalletState,
  IFormValues,
  IWalletBeneficiaryResponse,
  INetwork,
  FindBenNameType,
  INetworksResponse,
  IElevyState,
  IElevyResponse,
  IHandleFieldsChange,
  IWalletBenResponse,
  IMobileMoneyChargeResponse,
} from './MobileMoneyTransfer.model';
import { ON_SUBMIT, lookupBen, getReceipt, getTransactionFee } from './submitApi';
import validate from './validation';

const sortArrays = [
  'Network:',
  'Source Account:',
  'Beneficiary Name:',
  'Wallet Number:',
  'Narration:',
  'Amount:',
  'ELevy Fee:',
  'Transaction Fee:',
  'Total Amount:',
];

const excludedNetworks = ['Surfline', 'Glo', 'Busy'];

const MobileMoneyTransfer = () => {
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const [OTPError, setOTPError] = useState<IOTPError | null>(null);
  const [showLookupPrompt, setShowLookupPrompt] = useState(false);
  const [isFormDirty, setFormDirty] = useState(false);
  const [state, setState] = useState<IWalletState>({
    accounts: [],
    networks: [],
    beneficiaries: [],
  });
  const [elevyState, setElevyState] = useState<IElevyState>({
    charge: 0,
    taxableAmount: '',
    eLevyId: '',
  });

  const updateState = useCallback((update: Partial<IWalletState>) => setState((prev) => ({ ...prev, ...update })), []);
  const [loadingData, setLoadingData] = useState(false);
  const [confirmData, setConfirmData] = useState<ITransactionField[]>([]);
  const [transactionId, setTransactionId] = useState<number>(NaN);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;
  let mainForm!: FormApi<IFormValues, Partial<IFormValues>>;

  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };

  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
    setSuccessMsg('');
    setErrorMsg('');
  };

  const onResend = () => api.post('/customer/otp/generate');
  const openConfirmation = () => setConfirmationOpen(true);
  const closeConfirmation = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    } else {
      setConfirmationOpen(false);
    }
  };

  const closeLookupPrompt = useCallback(() => setShowLookupPrompt(false), []);

  const { ghsAccounts } = accountsStore;

  useEffect(() => updateState({ accounts: toJS(ghsAccounts) }), [ghsAccounts, updateState]);

  useEffect(() => {
    const fetchAccounts = () => {
      if (ghsAccounts.length) {
        // accounts loaded already
        return Promise.resolve();
      }
      return accountsStore.fetchAccounts();
    };
    const fetchNetworks = () =>
      api
        .get('/billspayment/networks', {}, true, false)
        .then((response: INetworksResponse) => {
          updateState({ networks: response.networks.filter(({ name }) => !excludedNetworks.includes(name)) });
        })
        .catch((error) => {
          console.error('error', error);
        });
    setLoadingData(true);

    Promise.all([fetchNetworks(), fetchAccounts()]).finally(() => {
      setLoadingData(false);
    });
  }, [ghsAccounts.length, updateState]);

  useEffect(() => {
    setLoadingData(true);

    const fetchBeneficiaries = () =>
      api
        .get(`/beneficiary/mobilemoney?loginId=${auth.data?.loginId || ''}`)
        .then((response: IWalletBeneficiaryResponse) => {
          updateState({ beneficiaries: response.beneficiaries });
        })
        .catch((e) => console.error(e));
    Promise.all([fetchBeneficiaries()]).finally(() => {
      setLoadingData(false);
    });
  }, [updateState]);

  const findBeneficiaryName: FindBenNameType = useCallback(
    (phoneNumber, networkCode = '') => {
      if (
        phoneNumber?.length >= PHONE_LENGTH &&
        !(phoneNumber === state.lastLookedUpPhone && networkCode === state.lastLookedUpNetwork)
      ) {
        lookupBen(networkCode, phoneNumber, 'A2W')
          .then((response: IWalletBenResponse) => {
            updateState({ accountName: response.accountName });
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(
            () =>
              updateState({
                isLookingUp: false,
                lastLookedUpPhone: phoneNumber,
                lastLookedUpNetwork: networkCode,
              }),
            // eslint-disable-next-line function-paren-newline
          );
        const savedBeneficiary = state.beneficiaries.find(
          (ben) => ben.phoneNumber === phoneNumber && ben.networkCode === networkCode,
        );
        if (savedBeneficiary && !(networkCode && savedBeneficiary.networkCode !== networkCode)) {
          updateState({
            accountName: savedBeneficiary.name,
            selectedBeneficiary: savedBeneficiary,
            lastLookedUpPhone: phoneNumber,
            lastLookedUpNetwork: savedBeneficiary.networkCode,
          });
        } else if (networkCode) {
          // lookup beneficiary
          updateState({
            isLookingUp: true,
            accountName: state.accountName,
            selectedBeneficiary: undefined,
          });
        }
      }
    },
    [state.lastLookedUpPhone, state.lastLookedUpNetwork, state.beneficiaries, state.accountName, updateState],
  );

  const networkTransactionMapper = (networks: INetwork[]) => (value: string | number, key: string) => {
    const transactionValue = transactionValuesMapper(value, key);
    switch (key) {
      case 'network':
      case 'networkCode':
        return networks.find((network) => network.code === transactionValue)?.name;
      default:
        return transactionValue;
    }
  };

  const getTransactionCharge = () => {
    const value = mainForm.getState().values;
    if (mainForm !== null && value?.phoneNumber && value?.amount) {
      setSubmitStatus(AxiosStatus.PENDING);
      getTransactionFee(value.amount, value.phoneNumber)
        .then((data: IMobileMoneyChargeResponse) => {
          updateState({ charge: data.charge });
        })
        .catch((error) => {
          console.error('Error occured', error);
        })
        .finally(() => {
          setSubmitStatus(AxiosStatus.DEFAULT);
        });
    }
  };

  const handleFieldsChange: IHandleFieldsChange = () => {
    const value = mainForm.getState().values;
    if (mainForm !== null && value?.networkCode && value?.sourceAccount && value?.phoneNumber && value?.amount) {
      const gipCodeForResponse = state.networks.find((network) => value?.networkCode === network.code);
      if (gipCodeForResponse) {
        const requestBody = {
          sourceAccount: value?.sourceAccount,
          destinationAccount: value?.phoneNumber,
          destinationGipCode: gipCodeForResponse.gipCode,
          amount: Number(value?.amount),
        };
        setSubmitStatus(AxiosStatus.PENDING);
        api
          .post('transfer/eLevyCharge', requestBody)
          .then((data: IElevyResponse) => {
            setElevyState({
              charge: data.charge,
              taxableAmount: data.taxableAmount,
              eLevyId: data.eLevyId,
            });
            mainForm.mutators.changeFieldValue('eLevyCharge', String(data.charge));
          })
          .then(() => {
            getTransactionCharge();
          })
          .catch((err) => {
            console.error(err);
          })
          .finally(() => {
            setSubmitStatus(AxiosStatus.DEFAULT);
          });
      }
    }
  };

  const getConfirmationData = (values: IFormValues) => {
    const readyValues = {
      ...values,
      beneficiaryName: state.accountName ?? '',
      transactionFee: state.charge || 0,
    };
    if (values) {
      readyValues.beneficiaryName = state.accountName ?? '';
      readyValues.totalAmount = Number(values.amount) + Number(values.eLevyCharge) + Number(state.charge);
    }
    return getTransactionsArray(readyValues, undefined, networkTransactionMapper(state.networks), sortArrays);
  };

  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
  }, []);

  const onSubmit = (values: IFormValues) => {
    const sourceAccount = ghsAccounts.find((account) => account.accountNumber === values.sourceAccount);
    if (sourceAccount) {
      const transactionAmount = Number(values.amount);
      const availableBalance = Number(sourceAccount.availableBalance);

      if (transactionAmount > availableBalance) {
        setSubmitStatus(AxiosStatus.FAILURE);
        setErrorMsg('Transfer failed.\nInsufficient funds to complete the transaction.');
        return;
      }
    }
    api
      .post('/customer/otp/generate')
      .then((resp: IApiResponse) => {
        setSuccessMsg(resp.description);
      })
      .catch((data) => {
        onRejectedSubmit(data);
      });
    setConfirmData(getConfirmationData(values));
    setSubmitStatus(AxiosStatus.DEFAULT);
    openConfirmation();
  };
  const onLookupPromptSubmit = useCallback(() => {
    closeLookupPrompt();
    openConfirmation();
  }, [closeLookupPrompt]);

  const onConfirm = useCallback(
    (values: IFormValues, form: FormApi<IFormValues>) => (otp: string, saveBeneficiary: boolean) => {
      setErrorMsg('');
      setSuccessMsg('');
      setSubmitStatus(AxiosStatus.PIN_PENDING);

      return validateOtpPin(otp)
        .then(() => {
          setSubmitStatus(AxiosStatus.PENDING);
          const submitApi = ON_SUBMIT();
          return submitApi[0](
            values,
            otp,
            saveBeneficiary,
            elevyState.eLevyId,
            state.charge || 0,
            state.accountName || '',
          )
            .then((data: IApiResponse) => {
              setSuccessMsg(data.description);
              setSubmitStatus(AxiosStatus.SUCCESS);
              if (data.transactionId) {
                setTransactionId(data.transactionId);
              }
              setConfirmData([]);
              form.reset();
            })
            .catch((error: IApiResponse) => {
              setErrorMsg(error.description);
              setSubmitStatus(AxiosStatus.FAILURE);
            });
        })
        .catch((error: IOTPError) => {
          setOTPError(error);
          handlePinError(setSubmitStatus, setErrorMsg);
        });
    },
    [elevyState.eLevyId, state.charge, state.accountName],
  );

  const onGetReceipt = useCallback(() => getReceipt(transactionId), [transactionId]);

  const onChangeForm = ({ dirty }: FormState<IFormValues>) => {
    if (isFormDirty !== dirty) {
      setFormDirty(dirty);
    }
  };

  const onRetryConfirm = () => setSubmitStatus(AxiosStatus.DEFAULT);

  return (
    <>
      {loadingData && <Loader />}
      {!loadingData && (
        <Form
          onSubmit={onSubmit}
          validate={validate}
          mutators={{ changeFieldValue }}
          render={({ handleSubmit, form, values, hasValidationErrors }) => {
            mainForm = mainForm || form;
            return (
              <form onSubmit={handleSubmit}>
                <>
                  <FieldsLeveler label="Transfer details">
                    {renderWalletField(values, state, form, findBeneficiaryName, handleFieldsChange)}
                  </FieldsLeveler>
                  <SubmitLeveler>
                    <Button
                      type="submit"
                      fullWidth
                      disabled={submitting || state.isLookingUp ||
                       !state.accountName || hasValidationErrors}
                    >
                      {submitting ? <CircularProgress size={20} color="inherit" /> : 'Make Payment'}
                    </Button>
                  </SubmitLeveler>
                </>
                <OTPConfirmationModal
                  open={isConfirmationOpen}
                  data={confirmData}
                  onClose={closeConfirmation}
                  OTPPinError={OTPError}
                  onResend={onResend}
                  onSubmit={onConfirm(values, form)}
                  submitStatus={submitStatus}
                  onSuccess={handleOnSuccess}
                  onRetry={onRetryConfirm}
                  getReceipt={onGetReceipt}
                  successText={successMsg}
                  failureText={errorMsg}
                  askSaveBeneficiary
                  disableSaveBeneficiary={!!state.selectedBeneficiary}
                  defaultSaveBeneficiary={state.selectedBeneficiary ? false : undefined}
                />
                <StatusModal
                  open={showStatusModal}
                  status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
                  onClose={onCloseStatusModal}
                  onRetry={onCloseStatusModal}
                  onRetryText="Close"
                >
                  {submitStatus === AxiosStatus.SUCCESS ? successMsg : (
                    <div style={{ whiteSpace: 'pre-line' }}>{errorMsg}</div>
                  )}
                </StatusModal>
                <WarningModal
                  open={showLookupPrompt}
                  submitName="Continue"
                  cancelName="Close"
                  onSubmit={onLookupPromptSubmit}
                  onClose={closeLookupPrompt}
                >
                  Mobile Money Name has not been validated. Would you like to continue?
                </WarningModal>
                <FormSpy subscription={{ dirty: true }} onChange={onChangeForm} />
              </form>
            );
          }}
        />
      )}
    </>
  );
};

export default observer(MobileMoneyTransfer);
